import { Injectable, computed, inject, signal } from '@angular/core';

import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';

import { KEYCLOAK_CONFIG } from './keycloak-config.token';
import { KeycloakTokenStore, StoredTokens } from './keycloak-token-store';
import {
  KeycloakEndpoints,
  TokenRequestError,
  TokenResponse,
  buildAuthorizeUrl,
  buildEndSessionUrl,
  buildEndpoints,
  decodeJwt,
  exchangeCode,
  isSilentSsoDeclined,
  parseCallback,
  randomString,
  refreshTokens,
  sha256Challenge,
} from './oauth.util';

const REFRESH_BUFFER_SECONDS = 60;
const MIN_VALIDITY_SECONDS = 30;
const WEB_VERIFIER_KEY = 'kc_pkce_verifier';
const WEB_STATE_KEY = 'kc_pkce_state';
const WEB_SILENT_SSO_KEY = 'kc_silent_sso_attempted';
/**
 * `window.location.assign()` unloads the page long before this elapses; the
 * timer only exists so a blocked navigation cannot hang APP_INITIALIZER
 * forever.
 */
const SILENT_SSO_NAVIGATION_TIMEOUT_MS = 8000;
/**
 * On Android the custom tab can be torn down by the OS around the same moment
 * the redirect deep link arrives, so `browserFinished` may race `appUrlOpen`.
 * Wait this long before treating a closed browser as a cancelled login.
 */
const CANCEL_GRACE_MS = 1500;

/**
 * `refreshed` — new tokens applied. `rejected` — Keycloak definitively refused
 * the refresh token (expired/revoked): the session is dead. `unavailable` —
 * transient failure (offline, 5xx): keep the session and retry later.
 */
type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private readonly config = inject(KEYCLOAK_CONFIG);
  private readonly store = inject(KeycloakTokenStore);

  private readonly endpoints: KeycloakEndpoints = buildEndpoints(
    this.config.issuer
  );

  private readonly tokens = signal<StoredTokens | null>(null);
  private parsedCache: Record<string, unknown> | null = null;
  private initPromise: Promise<boolean> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Promise<RefreshOutcome> | null = null;

  readonly isAuthenticated = computed(() => this.tokens() !== null);

  get token(): string | undefined {
    return this.tokens()?.accessToken;
  }

  get tokenParsed(): Record<string, unknown> | undefined {
    const token = this.tokens()?.accessToken;
    if (!token) {
      return undefined;
    }
    if (!this.parsedCache) {
      this.parsedCache = decodeJwt(token);
    }
    return this.parsedCache;
  }

  get authenticated(): boolean {
    return this.isAuthenticated();
  }

  get username(): string | undefined {
    return this.tokenParsed?.['preferred_username'] as string | undefined;
  }

  get company(): string | undefined {
    return (this.tokenParsed?.['company'] ??
      this.tokenParsed?.['companyUuid']) as string | undefined;
  }

  get groups(): string[] {
    const value = this.tokenParsed?.['groups'];
    return Array.isArray(value) ? (value as string[]) : [];
  }

  get companyRoles(): Record<string, string[]> | null {
    const value = this.tokenParsed?.['company_roles'];
    return value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, string[]>)
      : null;
  }

  async init(): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this.runInit();
    return this.initPromise;
  }

  async login(): Promise<void> {
    const { url, verifier, state } = await this.prepareAuthorize();

    if (!Capacitor.isNativePlatform()) {
      this.rememberWebFlow(verifier, state);
      window.location.assign(url);
      return;
    }

    const callbackUrl = await this.openAndAwaitCallback(url);
    const callback = parseCallback(callbackUrl);
    if (callback.error || !callback.code) {
      throw new Error(
        `Keycloak login failed: ${callback.error ?? 'no authorization code'}`
      );
    }
    if (callback.state !== state) {
      throw new Error('Keycloak login failed: state mismatch');
    }
    const response = await exchangeCode(
      this.endpoints,
      this.config.clientId,
      callback.code,
      this.redirectUri,
      verifier
    );
    await this.applyTokens(response);
  }

  async logout(): Promise<void> {
    this.clearRefreshTimer();
    const idToken = this.tokens()?.idToken ?? null;
    await this.clearSession();
    // An explicit sign-out must not be undone by the silent-SSO attempt that
    // runs on the next bootstrap.
    this.markSilentSsoAttempted();
    const url = buildEndSessionUrl(
      this.endpoints,
      this.config.clientId,
      this.redirectUri,
      idToken
    );

    if (!Capacitor.isNativePlatform()) {
      window.location.assign(url);
      return;
    }
    await this.openAndAwaitCallback(url).catch(() => undefined);
  }

  async getToken(): Promise<string> {
    const current = this.tokens();
    if (!current) {
      return '';
    }
    if (this.isExpired(MIN_VALIDITY_SECONDS)) {
      const outcome = await this.tryRefresh();
      if (outcome === 'rejected') {
        await this.clearSession();
        return '';
      }
      // 'unavailable' (offline / KC down): keep the session and hand back the
      // current token — the caller's 401 handling retries once we're back.
    }
    return this.tokens()?.accessToken ?? '';
  }

  private get redirectUri(): string {
    return Capacitor.isNativePlatform()
      ? this.config.redirectUri
      : `${window.location.origin}/`;
  }

  private get redirectScheme(): string {
    return this.config.redirectUri.split(':')[0];
  }

  private async runInit(): Promise<boolean> {
    const web = !Capacitor.isNativePlatform();
    if (web) {
      // A failed code exchange (expired/already-consumed code, KC hiccup) must
      // not reject the APP_INITIALIZER and abort bootstrap — strip the dead
      // callback params and fall through to the login page instead.
      await this.completeWebRedirect().catch((error) => {
        console.warn(
          '[keycloak-auth] login redirect completion failed:',
          error
        );
        this.stripCallbackParams();
      });
    }
    const stored = await this.loadStoredTokens();
    if (stored) {
      this.tokens.set(stored);
      this.parsedCache = null;
      if (this.isExpired(0)) {
        const outcome = await this.tryRefresh();
        if (outcome === 'rejected') {
          await this.clearSession();
        }
        // 'unavailable': keep the stored session; getToken() refreshes on demand.
      } else {
        this.scheduleRefresh();
      }
    }
    if (web && !this.isAuthenticated()) {
      await this.attemptSilentSso();
    }
    return this.isAuthenticated();
  }

  /**
   * The stored session lives in localStorage, which is per-origin, so every
   * tenant host (`butik.bee-king.eu`, `bananas.bee-king.eu`, …) starts out
   * empty even while Keycloak — one host shared by all of them — still has the
   * user signed in. One `prompt=none` round trip turns that SSO session into
   * tokens without ever rendering a form; Keycloak answers `login_required`
   * when there is nothing to reuse and we fall through to the login page.
   *
   * Deliberately a top-level navigation rather than the classic hidden iframe:
   * Safari and Chrome both block the third-party cookie an iframe would need.
   */
  private async attemptSilentSso(): Promise<void> {
    // An empty/relative issuer means this build has no Keycloak at all (the
    // demo bundle); navigating to an authorize URL built from it would leave
    // the app.
    if (!this.config.silentSso || !this.hasAbsoluteIssuer()) {
      return;
    }
    // Without a durable "already tried" marker this would redirect forever, so
    // a storage failure means we do not start at all.
    if (this.silentSsoAttempted() || !this.markSilentSsoAttempted()) {
      return;
    }
    const { url, verifier, state } = await this.prepareAuthorize('none');
    if (!this.rememberWebFlow(verifier, state)) {
      return;
    }
    window.location.assign(url);
    await new Promise<void>((resolve) => {
      setTimeout(resolve, SILENT_SSO_NAVIGATION_TIMEOUT_MS);
    });
  }

  private async prepareAuthorize(
    prompt?: 'none'
  ): Promise<{ url: string; verifier: string; state: string }> {
    const verifier = randomString(32);
    const challenge = await sha256Challenge(verifier);
    const state = randomString(16);
    const url = buildAuthorizeUrl(this.endpoints, {
      clientId: this.config.clientId,
      redirectUri: this.redirectUri,
      scope: this.config.scopes,
      codeChallenge: challenge,
      state,
      nonce: randomString(16),
      prompt,
    });
    return { url, verifier, state };
  }

  private async completeWebRedirect(): Promise<void> {
    const callback = parseCallback(window.location.href);
    // `login_required` and friends are how Keycloak declines a prompt=none
    // attempt. Expected answer, not an error: clean the URL and let the login
    // page render.
    if (isSilentSsoDeclined(callback.error)) {
      this.forgetWebFlow();
      this.stripCallbackParams();
      return;
    }
    const verifier = this.readWebFlowValue(WEB_VERIFIER_KEY);
    const expectedState = this.readWebFlowValue(WEB_STATE_KEY);
    if (!callback.code || !verifier) {
      return;
    }
    this.forgetWebFlow();
    if (callback.state !== expectedState) {
      return;
    }
    const response = await exchangeCode(
      this.endpoints,
      this.config.clientId,
      callback.code,
      this.redirectUri,
      verifier
    );
    await this.applyTokens(response);
    this.stripCallbackParams();
  }

  private stripCallbackParams(): void {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  private readWebFlowValue(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private rememberWebFlow(verifier: string, state: string): boolean {
    try {
      sessionStorage.setItem(WEB_VERIFIER_KEY, verifier);
      sessionStorage.setItem(WEB_STATE_KEY, state);
      return true;
    } catch {
      return false;
    }
  }

  private forgetWebFlow(): void {
    try {
      sessionStorage.removeItem(WEB_VERIFIER_KEY);
      sessionStorage.removeItem(WEB_STATE_KEY);
    } catch {
      return;
    }
  }

  private hasAbsoluteIssuer(): boolean {
    return /^https?:\/\//.test(this.config.issuer);
  }

  private silentSsoAttempted(): boolean {
    return this.readWebFlowValue(WEB_SILENT_SSO_KEY) === '1';
  }

  private markSilentSsoAttempted(): boolean {
    try {
      sessionStorage.setItem(WEB_SILENT_SSO_KEY, '1');
      return true;
    } catch {
      return false;
    }
  }

  private async openAndAwaitCallback(url: string): Promise<string> {
    const callback = this.waitForCallback();
    await Browser.open({ url });
    return callback;
  }

  private waitForCallback(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let urlHandle: PluginListenerHandle | null = null;
      let finishedHandle: PluginListenerHandle | null = null;
      let settled = false;

      const cleanup = async (): Promise<void> => {
        await urlHandle?.remove().catch(() => undefined);
        await finishedHandle?.remove().catch(() => undefined);
      };

      const onAppUrlOpen = async (event: { url: string }): Promise<void> => {
        if (settled || !event.url.startsWith(`${this.redirectScheme}:`)) {
          return;
        }
        settled = true;
        await cleanup();
        await Browser.close().catch(() => undefined);
        resolve(event.url);
      };

      // Without this, dismissing the in-app browser leaves login() pending
      // forever (spinner stuck, listener leaked). Grace period: see CANCEL_GRACE_MS.
      const onBrowserFinished = (): void => {
        setTimeout(() => {
          if (settled) {
            return;
          }
          settled = true;
          void cleanup();
          reject(new Error('Keycloak login was cancelled'));
        }, CANCEL_GRACE_MS);
      };

      void App.addListener('appUrlOpen', onAppUrlOpen).then((registered) => {
        urlHandle = registered;
        if (settled) {
          void registered.remove();
        }
      });
      void Browser.addListener('browserFinished', onBrowserFinished).then(
        (registered) => {
          finishedHandle = registered;
          if (settled) {
            void registered.remove();
          }
        }
      );
    });
  }

  async forceRefresh(): Promise<string> {
    const outcome = await this.tryRefresh();
    if (outcome === 'rejected') {
      await this.clearSession();
      return '';
    }
    if (outcome === 'unavailable') {
      // Transient failure: report no token but keep the stored session alive.
      return '';
    }
    return this.tokens()?.accessToken ?? '';
  }

  private tryRefresh(): Promise<RefreshOutcome> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.runRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async runRefresh(): Promise<RefreshOutcome> {
    const refreshToken = this.tokens()?.refreshToken;
    if (!refreshToken) {
      return 'rejected';
    }
    try {
      const response = await refreshTokens(
        this.endpoints,
        this.config.clientId,
        refreshToken
      );
      await this.applyTokens(response);
      return 'refreshed';
    } catch (error) {
      // Only a 4xx from the token endpoint proves the refresh token is dead;
      // a network error or 5xx must not destroy an otherwise-valid session.
      return error instanceof TokenRequestError && error.status < 500
        ? 'rejected'
        : 'unavailable';
    }
  }

  private async applyTokens(response: TokenResponse): Promise<void> {
    const now = Date.now();
    const stored: StoredTokens = {
      accessToken: response.access_token,
      refreshToken: response.refresh_token ?? null,
      idToken: response.id_token ?? null,
      expiresAt: now + response.expires_in * 1000,
      refreshExpiresAt: response.refresh_expires_in
        ? now + response.refresh_expires_in * 1000
        : null,
    };
    this.tokens.set(stored);
    this.parsedCache = null;
    try {
      await this.store.save(this.config.clientId, stored);
    } catch (error) {
      // The in-memory session still works for this run, but nothing survives a
      // restart — the difference between "signed in" and "signed in again
      // tomorrow", so say so instead of failing silently.
      console.warn(
        '[keycloak-auth] could not persist the session; it will not survive an app restart:',
        error
      );
    }
    this.scheduleRefresh();
  }

  private async loadStoredTokens(): Promise<StoredTokens | null> {
    try {
      return await this.store.load(this.config.clientId);
    } catch (error) {
      console.warn('[keycloak-auth] could not read the stored session:', error);
      return null;
    }
  }

  private async clearSession(): Promise<void> {
    this.clearRefreshTimer();
    this.tokens.set(null);
    this.parsedCache = null;
    try {
      await this.store.clear(this.config.clientId);
    } catch (error) {
      console.warn('[keycloak-auth] could not clear the stored session:', error);
    }
  }

  private isExpired(withinSeconds: number): boolean {
    const expiresAt = this.tokens()?.expiresAt;
    if (!expiresAt) {
      return true;
    }
    return Date.now() >= expiresAt - withinSeconds * 1000;
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();
    const expiresAt = this.tokens()?.expiresAt;
    if (!expiresAt) {
      return;
    }
    const refreshInMs = Math.max(
      expiresAt - Date.now() - REFRESH_BUFFER_SECONDS * 1000,
      0
    );
    if (refreshInMs > 0) {
      this.refreshTimer = setTimeout(() => {
        void this.getToken();
      }, refreshInMs);
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
